import type { Polygon, Position } from 'geojson';
import * as turf from '@turf/turf';
import * as z from 'zod';

const polygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
});

const multiPolygonSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(z.array(z.tuple([z.number(), z.number()])))),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
});

const featureSchema = z.object({
  type: z.literal('Feature'),
  geometry: z.union([polygonSchema, multiPolygonSchema]),
  properties: z.record(z.string(), z.unknown()).optional(),
});

const featureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(featureSchema),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
});

const COORDINATE_PATTERN =
  /\(\s*-?[0-9.]+\s+-?[0-9.]+(?:\s*,\s*-?[0-9.]+\s+-?[0-9.]+)*\s*\)/;
const POLYGON_PATTERN = new RegExp(
  `^(?:SRID=4326;\\s*)?POLYGON\\s*(\\(\\s*${COORDINATE_PATTERN.source}(?:\\s*,\\s*${COORDINATE_PATTERN.source})*\\s*\\))$`
);
const MULTIPOLYGON_PATTERN = new RegExp(
  `^(?:SRID=4326;\\s*)?MULTIPOLYGON\\s*\\(\\s*(\\(\\s*${COORDINATE_PATTERN.source}(?:\\s*,\\s*${COORDINATE_PATTERN.source})*\\s*\\)(?:\\s*,\\s*\\(\\s*${COORDINATE_PATTERN.source}(?:\\s*,\\s*${COORDINATE_PATTERN.source})*\\s*\\))*)\\s*\\)$`
);

const parseGeoJson = (geojson: string): Polygon[] => {
  try {
    const object = JSON.parse(geojson);

    const { success, data } = z
      .union([
        polygonSchema,
        multiPolygonSchema,
        featureSchema,
        featureCollectionSchema,
      ])
      .safeParse(object);
    if (!success) {
      return [];
    }

    switch (data.type) {
      case 'Polygon':
        return [data];
      case 'MultiPolygon':
        return data.coordinates.map((coordinates: Position[][]): Polygon => {
          return {
            type: 'Polygon',
            coordinates,
          };
        });
      case 'Feature':
        if (data.geometry.type === 'MultiPolygon') {
          return data.geometry.coordinates.map(
            (coordinates: Position[][]): Polygon => {
              return {
                type: 'Polygon',
                coordinates,
              };
            }
          );
        }
        if (data.geometry.type === 'Polygon') {
          return [data.geometry];
        }
        return [];
      case 'FeatureCollection':
        return data.features.flatMap((feature): Polygon[] => {
          if (feature.geometry.type === 'Polygon') {
            return [feature.geometry];
          } else if (feature.geometry.type === 'MultiPolygon') {
            return feature.geometry.coordinates.map(
              (coordinates: Position[][]): Polygon => {
                return {
                  type: 'Polygon',
                  coordinates,
                };
              }
            );
          }
          return [];
        });
      default:
        return [];
    }
  } catch {
    return [];
  }
};

const parseRing = (ring: string): Position[] => {
  return ring
    .trim()
    .replace(/^\(/, '')
    .replace(/\)$/, '')
    .split(/\s*,\s*/)
    .map((position: string): Position => {
      return position.trim().split(/\s+/).map(Number);
    });
};

const parsePolygonBody = (body: string): Position[][] => {
  return body
    .trim()
    .replace(/^\(/, '')
    .replace(/\)$/, '')
    .split(/\)\s*,\s*\(/)
    .map(parseRing);
};

const parseWkt = (wkt: string): Polygon[] => {
  const trimmed = wkt.trim();

  const match1 = trimmed.match(POLYGON_PATTERN);
  if (match1) {
    return [
      {
        type: 'Polygon',
        coordinates: parsePolygonBody(match1[1]),
      },
    ];
  }

  const match2 = trimmed.match(MULTIPOLYGON_PATTERN);
  if (match2) {
    return match2[1]
      .trim()
      .split(/\)\s*\)\s*,\s*\(\s*\(/)
      .map((polygonBody: string): Polygon => {
        return {
          type: 'Polygon',
          coordinates: parsePolygonBody(polygonBody),
        };
      });
  }

  return [];
};

const calculateCentroid = (polygon: Polygon): Position => {
  const centroid = turf.centroid(polygon);
  return centroid.geometry.coordinates;
};

const calculateDistance = (
  coord1: Position,
  coord2: Position,
  unit: 'meters' | 'kilometers'
): number => {
  const from = turf.point(coord1);
  const to = turf.point(coord2);
  return turf.distance(from, to, { units: unit });
};

const simplifyPolygon = (
  polygon: Polygon,
  simplifyFactor: number = 0.001
): Polygon => {
  return turf.simplify(polygon, {
    tolerance: simplifyFactor,
    highQuality: true,
  });
};

const truncatePolygon = (polygon: Polygon, precision: number = 6): Polygon => {
  return turf.truncate(polygon, { precision: precision, coordinates: 2 });
};

const createCircle = (
  center: Position,
  radius: number,
  options?: { steps: number; units?: 'meters' | 'kilometers' }
): Polygon => {
  return turf.circle(center, radius, options).geometry;
};

export {
  parseGeoJson,
  parseWkt,
  calculateCentroid,
  calculateDistance,
  simplifyPolygon,
  truncatePolygon,
  createCircle,
};
